import FlexBox from '@/atomic-components/FlexBox';
import { IOfferData, IOffers } from '@/components/ProductDisplayPage/model';
import { getColor } from '@/utils/theme/colors';
import { getStyle } from '@/utils/theme/fonts';
import { Box } from '@mui/material';
import { LencoTypography } from '@/atomic-components/LencoTypography';
import { useEffect, useState } from 'react';
import RightArrowIconWithColor from '../../../../../assets/pdp/rightArrowIconWithMintyColor.svg';
import OfferList from './offerList';
import DiscountBadge from '@/assets/offers/DiscountBadgeIcon';
import OfferCarousel from '@/atomic-components/carousels/wrappers/offer-carousel';
import OfferDrawerComponent from './offerDrawer';
import { Collection } from '@/services/catalogue/models/pdp';
import { dataLayerPushWithEvent } from '@/utils/dataLayer';
import TimeCountdown from '@/components/TimeCoundown';

export type OfferProps = {
	offers: IOfferData;
	productId: number;
	collections?: Collection[];
	lineOfProduct: string;
	productPrice: number;
	disountRatio: string;
	strikePrice: number;
	currentTenure: number;
	stockFlag: string;
};
const PDPOffers = (props: OfferProps) => {
	const [openAllOffers, setOpenAllOffers] = useState(false);
	const [selectedOfferToView, setSelectedOfferToView] = useState<IOffers>();
	const [codeCopied, setCodeCopied] = useState(false);

	const offerClicked = (offer: IOffers) => {
		setSelectedOfferToView(offer);
	};

	function closeOfferDetails() {
		setSelectedOfferToView(undefined);
	}

	const offersForUser = props?.offers?.data?.offers;

	const addEventAttributes = (expiryText?: string) => {
		const collection = props?.collections
			?.map((collection) => {
				return collection.label;
			})
			.join();

		const EventAttributesObject = {
			product_id: props.productId,
			collections: collection ? collection : null,
			lineofproduct: props?.lineOfProduct,
			final_price: props?.productPrice,
			discount_ratio: props?.productPrice,
			strike_price: props?.strikePrice,
			tenure_selected: props?.currentTenure,
			stock_flag: props?.stockFlag,
		};

		if (expiryText) {
			return {
				...EventAttributesObject,
				expiring_status: expiryText ? 'yes' : 'no',
			};
		}
		return EventAttributesObject;
	};

	const addEvent = (EventType: string, expiryText?: string) => {
		const attributes = addEventAttributes(expiryText);
		dataLayerPushWithEvent('moe_event', {
			moe_event_name: EventType,
			moe_event_data: { ...attributes },
		});
	};

	useEffect(() => {
		if (!!selectedOfferToView?.code) {
			setCodeCopied(false);
		}
	}, [selectedOfferToView]);

	if (!Array.isArray(offersForUser) || offersForUser?.length == 0) return null;

	return (
		<FlexBox direction='column'>
			<FlexBox sx={{ justifyContent: 'space-between', paddingBottom: '16px' }}>
				<LencoTypography
					sx={{ ...getStyle('H14_Medium'), color: getColor('Gray -1') }}
				>
					Offers & Discounts
				</LencoTypography>
				<LencoTypography
					onClick={() => {
						setOpenAllOffers(true);
						addEvent('PDP Offers and Discounts Page Opened');
					}}
					sx={{
						...getStyle('Small_Medium'),
						color: getColor('Minty +1'),
						cursor: 'pointer',
						'&:hover': {
							...getStyle('Small_SemiBold'),
							transition: '0.3 ease',
						},
					}}
				>
					({`${offersForUser.length} Available`}) See all{' '}
					<RightArrowIconWithColor />
				</LencoTypography>
			</FlexBox>

			{offersForUser.length > 1 ? (
				<Box width='376px'>
					<OfferCarousel spaceBetween={10} slidesPerView={1.75} dots={false}>
						{offersForUser.map((offer, index) => {
							return (
								<Box key={offer.code} sx={{ width: '100%', pt: '8px' }}>
									<FlexBox justifyContent='flex-end'>
										<Box sx={{ zIndex: 2, position: 'relative' }}>
											{offer.endDate && (
												<TimeCountdown
													endDate={offer.endDate as string}
													setOfferTimeOver={() => null}
												/>
											)}
										</Box>
									</FlexBox>
									<Box
										onClick={(e) => {
											e.stopPropagation();
											offerClicked(offer);
											addEvent('PDP Offers and Discounts Page Opened');
										}}
										sx={{
											borderRadius: '4px',
											width: '350px',
											background: `linear-gradient(-270deg, #FFF5B7 0%, #FFFBDB 100%)`,
											alignItem: 'start',
											justifyContent: 'start',
											cursor: 'pointer',
											'&:hover': {
												background: '#FFFFFF',
											},
										}}
									>
										<Box
											sx={{
												right: '0',
												bottom: '0',
												backgroundSize: 'contain',
												backgroundPositionX: 'right',
												backgroundRepeat: 'no-repeat',
												backgroundImage: `url(https://assets.furlenco.com/s3-furlenco-images/grogu/OfferIcon.svg)`,
											}}
										>
											<FlexBox
												sx={{
													padding: '22px 16px 26px 16px',
												}}
											>
												<DiscountBadge width={24} height={24} />
												<FlexBox
													direction='column'
													sx={{
														paddingLeft: '8px',
														paddingRight: '16px',
													}}
												>
													<LencoTypography
														sx={{
															...getStyle('P_SemiBold'),
															color: getColor('Gray 0'),
														}}
													>
														{offer.header}
													</LencoTypography>

													<Box
														sx={{
															width: '150px',
															overflow: 'hidden',
															textOverflow: 'ellipsis',
															whiteSpace: 'nowrap',
															color: getColor('Gray -2'),
															...getStyle('Tiny_SemiBold'),
														}}
													>
														{offer.description}
													</Box>

													<LencoTypography
														sx={{
															...getStyle('Tiny_SemiBold'),
															color: getColor('Gray -2'),
														}}
													>
														Use Coupon code : {offer.code}
													</LencoTypography>
												</FlexBox>
											</FlexBox>
										</Box>
									</Box>
								</Box>
							);
						})}
					</OfferCarousel>
				</Box>
			) : (
				<FlexBox sx={{ width: '100%' }}>
					<Box
						key={offersForUser[0].code}
						onClick={() => {
							offerClicked(offersForUser[0]);
						}}
						sx={{
							borderRadius: '4px',
							width: '300px',
							background: `linear-gradient(-270deg, #FFF5B7 0%, #FFFBDB 100%)`,
							alignItem: 'start',
							justifyContent: 'start',
							cursor: 'pointer',
							'&:hover': {
								background: '#FFFFFF',
							},
						}}
					>
						<Box
							sx={{
								right: '0',
								bottom: '0',
								backgroundSize: 'contain',
								backgroundPositionX: 'right',
								backgroundRepeat: 'no-repeat',
								backgroundImage: `url(https://assets.furlenco.com/s3-furlenco-images/grogu/OfferIcon.svg)`,
							}}
						>
							<FlexBox
								sx={{
									padding: '22px 16px 26px 16px',
								}}
							>
								<DiscountBadge width={24} height={24} />
								<FlexBox
									direction='column'
									sx={{
										paddingLeft: '8px',
									}}
								>
									<LencoTypography
										sx={{
											...getStyle('P_SemiBold'),
											color: getColor('Gray 0'),
										}}
									>
										{offersForUser[0].header}
									</LencoTypography>
									<LencoTypography
										sx={{
											...getStyle('Tiny_Medium'),
											color: getColor('Gray -2'),
										}}
									>
										{offersForUser[0].description}
									</LencoTypography>
									<LencoTypography
										sx={{
											...getStyle('Tiny_SemiBold'),
											color: getColor('Gray -2'),
										}}
									>
										Use Coupon code : {offersForUser[0].code}
									</LencoTypography>
								</FlexBox>
							</FlexBox>
						</Box>
					</Box>
				</FlexBox>
			)}

			<OfferList
				openAllOffers={openAllOffers}
				offers={props.offers}
				setOpenAllOffers={() => setOpenAllOffers(false)}
				addEvents={(eventType: string, expiryText?: string) =>
					addEvent(eventType, expiryText)
				}
			/>

			<OfferDrawerComponent
				offer={selectedOfferToView}
				open={!!selectedOfferToView}
				onClose={closeOfferDetails}
				addEvents={addEvent}
				codeCopied={codeCopied}
				setCodeCopied={(value: any) => setCodeCopied(!!value)}
			/>
		</FlexBox>
	);
};

export default PDPOffers;
